import { DirectionsBus, ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useAppDispatch } from 'app/config/store';
import { IFenceSyncStatus, IFenceTruckSyncStatus, getGroupFenceSyncStatus } from '../truck-group.reducer';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const POLL_INTERVAL_MS = 10000;

interface Props {
  groupId: number;
}

type FenceStatusKey = 'active' | 'pending' | 'pendingDelete' | 'empty';
type FenceStatusFilter = 'all' | Exclude<FenceStatusKey, 'empty'>;

const FENCE_STATUS_LABELS: Record<FenceStatusKey, string> = {
  active: 'Ativa',
  pending: 'Pendente',
  pendingDelete: 'Pendente de exclusão',
  empty: 'Sem ativos compatíveis',
};

const getFenceStatus = (trucks: IFenceTruckSyncStatus[]) => {
  if (trucks.some(truck => truck.syncStatus === 'PENDING_DELETE')) {
    return { key: 'pendingDelete' as const, label: FENCE_STATUS_LABELS.pendingDelete };
  }

  if (trucks.some(truck => truck.syncStatus === 'PENDING_SYNC')) {
    return { key: 'pending' as const, label: FENCE_STATUS_LABELS.pending };
  }

  if (trucks.some(truck => truck.syncStatus === 'SYNCED')) {
    return { key: 'active' as const, label: FENCE_STATUS_LABELS.active };
  }

  return { key: 'empty' as const, label: FENCE_STATUS_LABELS.empty };
};

const getFenceProgress = (trucks: IFenceTruckSyncStatus[]) => {
  const active = trucks.filter(truck => truck.syncStatus === 'SYNCED').length;
  return { active, total: trucks.length };
};

const compareText = (left?: string, right?: string) => (left || '').localeCompare(right || '', 'pt-BR', { sensitivity: 'base', numeric: true });

export const TruckGroupFenceSyncPanel = ({ groupId }: Props) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const filterBorderColor = isDarkMode ? alpha(theme.palette.common.white, 0.24) : theme.palette.divider;
  const selectedAllBg = isDarkMode ? theme.palette.grey[800] : theme.palette.grey[900];
  const selectedAllColor = theme.palette.getContrastText(selectedAllBg);
  const statusColors: Record<FenceStatusKey, string> = {
    active: theme.palette.success.main,
    pending: theme.palette.warning.main,
    pendingDelete: theme.palette.error.main,
    empty: theme.palette.text.disabled,
  };
  const syncConfig: Record<IFenceTruckSyncStatus['syncStatus'], { color: string; label: string }> = {
    PENDING_SYNC: { color: statusColors.pending, label: FENCE_STATUS_LABELS.pending },
    SYNCED: { color: statusColors.active, label: FENCE_STATUS_LABELS.active },
    PENDING_DELETE: { color: statusColors.pendingDelete, label: FENCE_STATUS_LABELS.pendingDelete },
  };
  const getSelectedStatusSx = (status: Exclude<FenceStatusKey, 'empty'>) => ({
    '&.Mui-selected, &.Mui-selected:hover': {
      bgcolor: statusColors[status],
      color: theme.palette.getContrastText(statusColors[status]),
      borderColor: `${statusColors[status]} !important`,
    },
  });
  const [data, setData] = useState<IFenceSyncStatus[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FenceStatusFilter>('all');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const sortedData = useMemo(
    () =>
      [...(data || [])]
        .map(fence => ({
          ...fence,
          trucks: [...(fence.trucks || [])].sort(
            (left, right) => compareText(left.licensePlate, right.licensePlate) || compareText(left.syncStatus, right.syncStatus)
          ),
        }))
        .sort(
          (left, right) =>
            compareText(left.fenceName, right.fenceName) ||
            compareText(left.fenceType, right.fenceType) ||
            (left.fenceId ?? Number.MAX_SAFE_INTEGER) - (right.fenceId ?? Number.MAX_SAFE_INTEGER)
        ),
    [data]
  );

  const fetchData = useCallback(async () => {
    const result = await dispatch(getGroupFenceSyncStatus(groupId));
    if (getGroupFenceSyncStatus.fulfilled.match(result)) {
      setData(result.payload.data);
    }
    setLoading(false);
  }, [groupId]);

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchData]);

  useEffect(() => {
    setActiveFilter('all');
  }, [groupId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={20} color="secondary" />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box px={2.5} py={1.5}>
        <Typography variant="caption" color="text.secondary">
          Nenhuma cerca vinculada a este grupo.
        </Typography>
      </Box>
    );
  }

  const fenceStatuses = sortedData.map(fence => getFenceStatus(fence.trucks));
  const activeCount = fenceStatuses.filter(status => status.key === 'active').length;
  const pendingCount = fenceStatuses.filter(status => status.key === 'pending').length;
  const pendingDeleteCount = fenceStatuses.filter(status => status.key === 'pendingDelete').length;
  const filteredData = activeFilter === 'all' ? sortedData : sortedData.filter(fence => getFenceStatus(fence.trucks).key === activeFilter);

  return (
    <Box px={2.5} pb={2} display="flex" flexDirection="column" gap={0.75}>
      <Box sx={{ display: 'flex', alignItems: 'center', overflowX: 'auto', '&::-webkit-scrollbar': { height: 0 } }}>
        <ToggleButtonGroup
          value={activeFilter}
          exclusive
          onChange={(_, value) => setActiveFilter((value as FenceStatusFilter | null) ?? 'all')}
          size="small"
          sx={{
            flexShrink: 0,
            '& .MuiToggleButtonGroup-grouped': {
              border: `1px solid ${filterBorderColor} !important`,
              '&:not(:first-of-type)': {
                borderLeft: `1px solid ${filterBorderColor} !important`,
                marginLeft: '-1px',
              },
            },
            '& .MuiToggleButton-root': {
              px: 1.5,
              py: 0.3,
              fontSize: 12,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              color: 'text.secondary',
              lineHeight: 1.4,
            },
            '& .MuiToggleButton-root.Mui-selected': {
              color: `${theme.palette.common.white} !important`,
            },
          }}
        >
          <ToggleButton
            value="all"
            sx={{
              '&.Mui-selected, &.Mui-selected:hover': {
                backgroundColor: `${selectedAllBg} !important`,
                color: `${selectedAllColor} !important`,
                borderColor: `${filterBorderColor} !important`,
              },
            }}
          >
            Todas ({data.length})
          </ToggleButton>
          <ToggleButton value="active" sx={getSelectedStatusSx('active')}>
            Ativas ({activeCount})
          </ToggleButton>
          <ToggleButton value="pending" sx={getSelectedStatusSx('pending')}>
            Pendentes ({pendingCount})
          </ToggleButton>
          <ToggleButton value="pendingDelete" sx={getSelectedStatusSx('pendingDelete')}>
            Pend. exclusão ({pendingDeleteCount})
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {filteredData.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ py: 1 }}>
          Nenhuma cerca neste filtro.
        </Typography>
      )}

      {filteredData.map(fence => {
        const fenceStatus = getFenceStatus(fence.trucks);
        const fenceProgress = getFenceProgress(fence.trucks);
        const fenceName = fence.fenceName || `Cerca #${fence.fenceId}`;

        return (
          <Accordion
            key={`${fence.fenceType ?? 'CIRCLE'}-${fence.fenceId}`}
            disableGutters
            elevation={0}
            sx={{
              backgroundColor: 'transparent',
              borderTop: `1px solid ${filterBorderColor}`,
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': {
                minHeight: 42,
                px: 1,
                borderRadius: '6px',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              },
              '& .MuiAccordionSummary-content': {
                my: 0.75,
                minWidth: 0,
              },
              '& .MuiAccordionSummary-expandIconWrapper': {
                flexShrink: 0,
              },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore fontSize="small" />}>
              <Box
                display="grid"
                alignItems="center"
                columnGap={0.75}
                minWidth={0}
                width="100%"
                sx={{ gridTemplateColumns: 'auto minmax(0, 1fr) auto' }}
              >
                <Tooltip title={fenceStatus.label} placement="right">
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: statusColors[fenceStatus.key],
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
                <Box display="flex" alignItems="baseline" gap={0.4} minWidth={0} overflow="hidden">
                  <Tooltip title={fenceName} placement="top">
                    <Typography variant="caption" fontWeight={600} color="text.primary" noWrap sx={{ minWidth: 0, display: 'block' }}>
                      {fenceName}
                    </Typography>
                  </Tooltip>
                  <Chip
                    label={fence.fenceType === 'POLYGON' ? 'Poligonal' : 'Circular'}
                    size="small"
                    variant="outlined"
                    sx={{ height: 17, fontSize: '0.58rem', borderRadius: '4px', flexShrink: 0 }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1, flexShrink: 0 }}
                  >
                    ({fenceProgress.active}/{fenceProgress.total})
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ pr: 0.75, fontSize: '0.68rem', maxWidth: { xs: 62, sm: 'none' }, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  noWrap
                >
                  {fenceStatus.label}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1, px: 0 }}>
              {fence.trucks.length === 0 ? (
                <Box sx={{ ml: 2.5, pl: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Nenhum ativo compatível com cerca neste grupo.
                  </Typography>
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" sx={{ ml: 2.5, pl: 1.5 }}>
                  {fence.trucks.map((truck, idx) => {
                    const config = syncConfig[truck.syncStatus] ?? { color: statusColors.empty, label: truck.syncStatus };
                    return (
                      <Box key={idx}>
                        {idx > 0 && <Divider sx={{ opacity: 0.08 }} />}
                        <Box display="flex" alignItems="center" gap={0.75} py={0.75} sx={{ minHeight: 32 }}>
                          <Tooltip title={config.label} placement="right">
                            <Box
                              sx={{
                                width: 7,
                                height: 7,
                                borderRadius: '50%',
                                backgroundColor: config.color,
                                flexShrink: 0,
                              }}
                            />
                          </Tooltip>
                          <DirectionsBus sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }} />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ fontSize: '0.7rem', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {truck.licensePlate}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};
